import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import ClientConfigurationForm from "../components/ClientConfiguration/ClientConfigurationForm";
import { ClientConfiguration } from "../models/ClientConfiguration/ClientConfiguration";
import { FlaggingConfiguration, FlagType } from "../models/ClientConfiguration/FlaggingConfiguration";
import ClientConfigurationsRepository from "../repositories/ClientConfigurationsRepository";
import OrganizationsRepository from "../repositories/OrganizationsRepository";
import { ImplementationType } from "../models/OrganizationConfiguration/ImplementationType";
import { UserRole } from "../models/User";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useDetailBreadcrumb } from "../contexts/DetailBreadcrumbContext";
import { QUERY_KEYS } from "../constants/QueryKeys";

interface ClientConfigurationPageProps {
  configurationId?: string;
  organizationId?: string;
  userRole: UserRole;
  isAssignedUser: boolean;
}

const ClientConfigurationPage: React.FC<ClientConfigurationPageProps> = ({
  configurationId,
  organizationId,
  userRole,
  isAssignedUser,
}) => {
  const configurationsRepository = new ClientConfigurationsRepository();
  const organizationRepository = new OrganizationsRepository();
  const { setEntityLabel } = useDetailBreadcrumb();
  let selectedConfig = undefined;

  const {
    data: configuration,
    isLoading,
    isError,
    error,
  } = useQuery({ queryKey: [QUERY_KEYS.CLIENT_CONFIGURATIONS, configurationId], queryFn:
    async () => {
      if (configurationId)
        return await configurationsRepository.getClientConfiguration(configurationId ?? "");
    }, enabled: !!configurationId,
      refetchOnMount: true,
      refetchInterval: 2 * 60 * 1000,
      refetchIntervalInBackground: false, });

  const { data: implementationType } = useQuery({ queryKey: [QUERY_KEYS.ORGANIZATION_IMPLEMENTATION_TYPE, organizationId], queryFn:
    async () => {
      if (organizationId)
        return await organizationRepository.getOrganizationImplementationType(organizationId ?? "");
    }, refetchOnMount: true,
      refetchInterval: 2 * 60 * 1000,
      refetchIntervalInBackground: false, });

  useEffect(() => {
    if (configuration?.name) {
      setEntityLabel(configuration.name);
    } else if (!configurationId) {
      setEntityLabel("New Configuration");
    }
    return () => setEntityLabel(null);
  }, [configuration, configurationId]);

  if (isLoading)
    return (
      <div className="flex-1 min-h-0 flex flex-col gap-4 p-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[400px] w-full rounded-md" />
      </div>
    );

  if (isError)
    return (
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">Failed to load configuration.</p>
        <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  const defaultFlags = [
    { flagType: FlagType.MigrationEntityWithMoreThan10KFiles },
    { flagType: FlagType.DicomFile },
  ] as FlaggingConfiguration[];
  if (implementationType === ImplementationType.TrialWorks) {
    defaultFlags.push({
      flagType: FlagType.FileExtension,
      flagProperties: [{ key: "extension", value: ".msg" }],
    });
  }
  selectedConfig =
    !!configurationId && configuration
      ? configuration
      : ({
        id: "",
        name: "",
        isDefault: true,
        dateCreated: "",
        migrateCases: true,
        migrateProviders: false,
        migrateTemplateFiles: false,
        migrateFirmDocuments: false,
        migrateIntakes: false,
        discoverCases: true,
        discoverProviders: false,
        discoverTemplateFiles: false,
        discoverFirmDocuments: false,
        discoverIntakes: false,
        uploadStarterTemplates: false,
        uploadFilesMetadataToSharepoint: true,
        uploadEmptyFoldersToSharepoint: false,
        sendDebugLogs: false,
        sharepointAppName: "",
        flags: defaultFlags,
        sharepointRootSiteName: "CloudDocs",
        casesSharepointRootDriveName: "CaseFiles",
        providersSharepointRootDriveName: "ProviderFiles",
        firmDocumentsSharepointRootDriveName: "FirmDocuments",
        templateFilesSharepointRootDriveName: "TemplateFiles",
        fileTypeFilters: [{ fileExtension: ".wpd" }],
      } as ClientConfiguration);

  return (
    <div className="-mx-6 md:-mx-12 -mb-8 flex-1 min-h-0 overflow-y-auto">
      <ClientConfigurationForm
        organizationId={organizationId ?? ""}
        implementationType={implementationType ?? ImplementationType.DocsToNeos}
        configuration={selectedConfig}
        key={implementationType}
        userRole={userRole}
        isAssignedUser={isAssignedUser}
      />
    </div>
  );
};

export default ClientConfigurationPage;
